import { Link } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CurrencyDollarIcon, SpinnerGapIcon, ChartLineUpIcon, WalletIcon, TrophyIcon } from "@phosphor-icons/react";
import { getClientId } from "../lib/clientId";
import { fetchMillyBoard } from "../lib/api";
import { QuickBuyLink } from "../components/TradeButton";
import { useSeo } from "../lib/useSeo";

const GOLD = "#F0C000";
const short = (a) => (a && a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");
const timeAgo = (ts) => {
  if (!ts) return "—";
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const WINDOWS = [
  { h: 6, label: "6h" },
  { h: 24, label: "24h" },
  { h: 168, label: "7d" },
];

const MillyRow = ({ r, rank }) => (
  <div
    data-testid={`milly-row-${r.walletAddress}-${r.tokenMint}`}
    className={`relative flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 border transition-colors hover:bg-white/[0.03] ${r.running ? "milly-glow" : ""}`}
    style={{ borderColor: r.running ? `${GOLD}88` : `${GOLD}33`, background: r.running ? `linear-gradient(90deg, ${GOLD}1f, transparent)` : rank <= 3 ? `linear-gradient(90deg, ${GOLD}14, transparent)` : "transparent" }}
  >
    <div className="w-7 text-center font-heading font-black text-lg shrink-0" style={{ color: rank <= 3 ? GOLD : "#666" }}>
      {rank}
    </div>
    {r.tokenImage ? (
      <img src={r.tokenImage} alt="" className="w-9 h-9 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
    ) : (
      <div className="w-9 h-9 flex items-center justify-center border shrink-0" style={{ borderColor: `${GOLD}55`, color: GOLD }}>
        <CurrencyDollarIcon size={18} weight="bold" />
      </div>
    )}
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to={`/coin/${r.tokenMint}`} className="font-heading font-bold hover:text-neon transition-colors">${r.tokenSymbol || "token"}</Link>
        <span className="font-mono text-[10px] px-1.5 py-0.5 tracking-wide font-bold" style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}88`, color: GOLD }}>
          {r.tier}+ SOL
        </span>
        {r.running && (
          <span data-testid={`milly-runner-badge-${r.tokenMint}`} title="MILLY RUNNER — this token is running AND smart money is loading up" className="font-mono text-[10px] px-1.5 py-0.5 font-bold animate-pulse" style={{ background: `${GOLD}33`, border: `1px solid ${GOLD}`, color: GOLD }}>
            🏃💰 RUNNER
          </span>
        )}
        {r.proven && (
          <span title={`Proven wallet — PnL score ${r.walletScore}`} className="font-mono text-[10px] px-1.5 py-0.5 bg-neon/10 border border-neon/40 text-neon">🔥 proven</span>
        )}
      </div>
      <div className="font-mono text-[11px] text-muted-foreground truncate">
        <WalletIcon size={11} weight="bold" className="inline mb-0.5 mr-1" />
        {r.walletLabel || short(r.walletAddress)}
        {r.stacked ? <> · <span style={{ color: GOLD }}>{r.buys} buys stacking</span> (max {r.maxSol})</> : " · single buy"}
        {" · "}{timeAgo(r.lastTs)}
      </div>
    </div>
    <div className="text-right shrink-0">
      <div className="font-heading font-black text-lg leading-none" style={{ color: GOLD }}>{r.totalSol}</div>
      <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">SOL in</div>
    </div>
    <div className="flex items-center gap-1 shrink-0">
      <QuickBuyLink coin={{ address: r.tokenMint, url: r.url }} testid={`milly-buy-${r.tokenMint}`} />
      <Link to={`/coin/${r.tokenMint}`} title="Open chart & Degen Score" className="p-1.5 text-muted-foreground hover:text-neon transition-colors" data-testid={`milly-chart-${r.tokenMint}`}>
        <ChartLineUpIcon size={16} weight="bold" />
      </Link>
    </div>
  </div>
);

export default function MillyBoard() {
  const clientId = getClientId();
  const [windowH, setWindowH] = useState(24);
  const [runnersOnly, setRunnersOnly] = useState(false);

  useSeo({
    title: "💰 Milly's Call — Biggest Smart-Money Buys | AlphaApeing",
    description: "A live leaderboard of the biggest SOL buys from proven wallets — see where the smart money is loading up right now.",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["millyBoard", clientId, windowH],
    queryFn: () => fetchMillyBoard(clientId, windowH, 5),
    refetchInterval: 30000,
  });
  const rows = data?.rows || [];
  const runnerCount = rows.filter((r) => r.running).length;
  const visibleRows = runnersOnly ? rows.filter((r) => r.running) : rows;

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="milly-board-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center text-black" style={{ background: GOLD }}>
            <CurrencyDollarIcon size={24} weight="bold" />
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">
            MILLY'S <span style={{ color: GOLD }}>CALL</span>
          </h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          Where the <span style={{ color: GOLD }} className="font-semibold">smart money is loading up right now</span> — the biggest single & stacking
          SOL buys from the wallets you track, ranked by total size. Cuts through the 0.1-SOL noise so you catch tokens
          before they run. Turn on the Telegram ping on the <Link to="/alerts" className="text-neon hover:underline">Alerts page</Link>.
        </p>
      </div>

      {/* window filter + stats */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="milly-window-filter">
          {WINDOWS.map((w) => (
            <button
              key={w.h}
              data-testid={`milly-window-${w.h}`}
              onClick={() => setWindowH(w.h)}
              className={`px-3 py-1.5 text-xs font-mono font-semibold border transition-colors ${
                windowH === w.h ? "text-black" : "text-muted-foreground border-white/15 hover:text-white"
              }`}
              style={windowH === w.h ? { background: GOLD, borderColor: GOLD } : {}}
            >
              {w.label}
            </button>
          ))}
          <button
            data-testid="milly-runners-only-chip"
            onClick={() => setRunnersOnly((v) => !v)}
            title="Show only tokens currently running while smart money loads up"
            className={`px-3 py-1.5 text-xs font-mono font-semibold border transition-colors ${
              runnersOnly ? "text-black" : "text-muted-foreground border-white/15 hover:text-white"
            } ${runnerCount > 0 && !runnersOnly ? "animate-pulse" : ""}`}
            style={runnersOnly ? { background: GOLD, borderColor: GOLD } : runnerCount > 0 ? { borderColor: `${GOLD}88`, color: GOLD } : {}}
          >
            🏃💰 Runners{runnerCount > 0 ? ` (${runnerCount})` : ""}
          </button>
        </div>
        {data && rows.length > 0 && (
          <div className="font-mono text-[11px] text-muted-foreground flex items-center gap-3" data-testid="milly-stats">
            <span><b style={{ color: GOLD }}>{data.totalSol}</b> SOL deployed</span>
            <span>·</span>
            <span>{data.walletCount} wallet{data.walletCount === 1 ? "" : "s"} loading up</span>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><SpinnerGapIcon size={32} className="animate-spin" style={{ color: GOLD }} /></div>
      ) : visibleRows.length === 0 ? (
        <div className="border border-white/10 bg-card py-20 text-center text-muted-foreground font-mono text-sm px-6 flex flex-col items-center gap-3">
          <CurrencyDollarIcon size={30} style={{ color: GOLD }} />
          {runnersOnly
            ? <>No <span style={{ color: GOLD }}>🏃💰 runners</span> right now. A row lights up here when a tracked token is running AND smart money is stacking it.</>
            : <>No big buys in the last {WINDOWS.find((w) => w.h === windowH)?.label}. When a tracked wallet drops 5+ SOL on a token, it'll rank here.</>}
          {runnersOnly
            ? <button onClick={() => setRunnersOnly(false)} className="text-neon hover:underline" data-testid="milly-clear-runners">Show all big buys →</button>
            : <Link to="/smart-money" className="text-neon hover:underline">Track more Smart Money wallets →</Link>}
        </div>
      ) : (
        <div className="border border-white/5 divide-y divide-white/5" data-testid="milly-rows">
          {visibleRows.map((r, i) => <MillyRow key={`${r.walletAddress}-${r.tokenMint}`} r={r} rank={i + 1} />)}
        </div>
      )}

      <div className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground border-t border-white/5 pt-4">
        <TrophyIcon size={13} style={{ color: GOLD }} />
        Ranked by total SOL bought per wallet → token within the window. Community-flagged scams auto-hidden.
      </div>
    </div>
  );
}
